// The hero media for each trunk, in one place, so swapping a licensed clip for
// our own shoot is a one line change and never a layout change.
//
// ⚠️ EVERY FILE NEEDS A LINE IN public/hero/CREDITS.md: source URL, licence name,
// date downloaded. Royalty free is not the same as unaccountable, and a licence
// we cannot evidence is one we cannot defend.
//
// Encode to the budget in docs/MARKETING-SITE-SPEC.md §6.2 and §6.5:
//   still    AVIF + WebP + JPEG, 1600 and 2400 wide plus a 4:5 mobile crop, the
//            1600 wide AVIF under 180KB. It is the LCP element
//   video    WebM/VP9 + MP4/H.264, 6 to 10 seconds, seamless loop, NO audio
//            track, under 2.5MB at 1600 wide
//
// ⚠️ DO NOT hand write the still URLs. `scripts/encode-hero.sh` emits them from
// one naming convention and `still()` below rebuilds exactly that convention. If
// you rename an output there, change it here in the same commit or the hero
// silently falls back to a navy gradient.
//
// `null` renders the navy fallback rather than a broken image, so the page can be
// reviewed before the assets land.

// Builds the <picture> payload for one hero still from the encode script's
// output names: <base>-1600.*, <base>-2400.*, <base>-mobile.*
//
// `width`/`height` are the intrinsic size of the DEFAULT source (1600 wide), and
// the mobile crop carries its own, because a <source> that lies about its shape
// is a layout shift waiting for a slow connection. Everything is a srcset string
// rather than an array so the component can hand it straight to the DOM and to
// the preload tag without reformatting, which is how the two drift apart.
function still(base, { width, height, mobileWidth = 900, mobileHeight = 1125 }) {
  const set = (ext) => `/hero/${base}-1600.${ext} 1600w, /hero/${base}-2400.${ext} 2400w`
  return {
    width,
    height,
    avif: set('avif'),
    webp: set('webp'),
    jpg: set('jpg'),
    // The <img src>, and the only thing a browser with no srcset support gets.
    src: `/hero/${base}-1600.jpg`,
    // The art directed 4:5 crop, per §6.1 "never centre crop". One width, no
    // srcset: a phone that wants more than 900 pixels across is rare enough that
    // the second candidate costs more in decisions than it saves in pixels.
    mobile: {
      width: mobileWidth,
      height: mobileHeight,
      avif: `/hero/${base}-mobile.avif`,
      webp: `/hero/${base}-mobile.webp`,
      jpg: `/hero/${base}-mobile.jpg`,
    },
  }
}

// The Find work hero ROTATES (founder 2026-10-02: one clip is a wallpaper).
// Three clips, deliberately in this order, because they read as one day:
//   1. walking out of the building, done for the day
//   2. on the bus home, checking the phone  (same actor as 1)
//   3. a different person, different light, so it does not become one story
//
// ⚠️ ALL THREE ARE PLACEHOLDERS (Coverr, free licence, see public/hero/CREDITS.md).
// They clear the §6.1 ban list and two of the three read unmistakably UK, which
// most stock does not. They are not the shipping hero: nobody in them is a
// healthcare professional and none of the places is a workplace.
//
// ⚠️ The sources are 1600x900, so the 2400 wide variants are upscales today. The
// slot is wired anyway so that a real shoot drops in without touching markup.
//
// `poster` stays a plain JPEG: it is the <video poster> attribute, which takes a
// single URL with no format negotiation, and it is only ever fetched after load.
// The still that a visitor actually sees first is the <picture> built from
// `still`, which is AVIF for almost everybody.
// `alt` is a message key (src/i18n/messages/en/ui.js `media`), read through
// useT() where the picture is drawn, so every language describes the photo.
export const HERO_WORK = [
  {
    poster: '/hero/work-1-poster.jpg',
    still: still('work-1', { width: 1600, height: 900 }),
    webm: '/hero/work-1.webm',
    mp4: '/hero/work-1.mp4',
    alt: 'ui.media.work1',
  },
  {
    poster: '/hero/work-2-poster.jpg',
    still: still('work-2', { width: 1600, height: 900 }),
    webm: '/hero/work-2.webm',
    mp4: '/hero/work-2.mp4',
    alt: 'ui.media.work2',
  },
  {
    poster: '/hero/work-3-poster.jpg',
    still: still('work-3', { width: 1600, height: 900 }),
    webm: '/hero/work-3.webm',
    mp4: '/hero/work-3.mp4',
    alt: 'ui.media.work3',
  },
]

// Get cover: three clips, rotated like Find work's (founder 2026-10-06). The
// reception desk first: a calm front of house and money coming in, with a clean
// left third for the headline. Then a patient shown into the chair, then a nurse
// preparing the surgery: the day running smoothly. Sources and licences in public/hero/CREDITS.md.
export const HERO_COVER = [
  {
    poster: '/hero/cover-1-poster.jpg',
    still: still('cover-1', { width: 1600, height: 900, mobileWidth: 760, mobileHeight: 950 }),
    webm: '/hero/cover-1.webm',
    mp4: '/hero/cover-1.mp4',
    alt: 'ui.media.cover1',
  },
  {
    poster: '/hero/cover-2-poster.jpg',
    still: still('cover-2', { width: 1600, height: 900, mobileWidth: 760, mobileHeight: 950 }),
    webm: '/hero/cover-2.webm',
    mp4: '/hero/cover-2.mp4',
    alt: 'ui.media.cover2',
  },
  {
    poster: '/hero/cover-3-poster.jpg',
    still: still('cover-3', { width: 1600, height: 900, mobileWidth: 760, mobileHeight: 950 }),
    webm: '/hero/cover-3.webm',
    mp4: '/hero/cover-3.mp4',
    alt: 'ui.media.cover3',
  },
]

// About: TempDash as a technology company, not a clinic (founder 2026-10-07: "the
// About Us video shouldn't have somebody in a dental chair"). Mixkit clip 918, a
// busy open plan startup office; credited in public/hero/CREDITS.md.
export const HERO_ABOUT = {
  poster: '/hero/about-1-poster.jpg',
  still: still('about-1', { width: 1600, height: 900 }),
  webm: '/hero/about-1.webm',
  mp4: '/hero/about-1.mp4',
  alt: 'ui.media.about',
}

// FAQ: a friendly person answering on a call (founder 2026-10-07: the walking
// clip did not fit "Questions, answered"). Mixkit clip 4844; credited in
// public/hero/CREDITS.md.
export const HERO_FAQ = {
  poster: '/hero/faq-1-poster.jpg',
  still: still('faq-1', { width: 1600, height: 900 }),
  webm: '/hero/faq-1.webm',
  mp4: '/hero/faq-1.mp4',
  alt: 'ui.media.faq',
}
